/* oxlint-disable no-console */
// libraries
import { Fragment, useEffect } from "react";
import {
  Outlet,
  ReactLocation,
  Route,
  Router,
  createBrowserHistory,
} from "@tanstack/react-location";

// contexts
import { OfflineSupportProvider } from "../contexts/offline-support/offline-support.context";

// components
import { Auth } from "./Auth";
import LoadingWrapper from "../components/LoadingWrapper";

// utilities
import { semiRandomId } from "../utilities/ids";
const start = Date.now();

// loaders
import { auth } from "../services/firebase.service";
import routeRedirectElement from "./asyncRedirect";

// constants
import { preservedRoutes, regularRoutes, routeMap, ROUTES } from "./utilities";

// types
import { PathConfig, defaultPageConfig } from "./types";
import {
  LOCATION_RESTORE_KEY,
  setLocalStorageItem,
} from "../hooks/useLocalStorage";

// oxlint-disable-next-line typescript/no-explicit-any
type FileExports = { default?: any; config?: PathConfig; [key: string]: any };

/**
 * Components
 */
const App = preservedRoutes._app || Fragment;

const NoopFragment: React.FC<
  React.PropsWithChildren & {
    // oxlint-disable-next-line typescript/no-explicit-any
    params: { [key: string]: any };
    config?: PathConfig;
  }
> = ({ children }) => {
  return <Fragment>{children}</Fragment>;
};

/**
 * Tanstack route generation
 */
const generatedRoutes = Object.keys(routeMap).reduce<Route[]>((routes, key) => {
  const route = ROUTES[key];
  const path = routeMap[key];

  path.split("/").reduce((acc: Route[], fragment, idx, keys) => {
    if (fragment === "") {
      return acc;
    }

    const routeFragment = acc?.find((rte) => rte.path == fragment);

    if (keys.length - 1 === idx) {
      const newRoute: Route = {
        path: "/",
        children: [],
        element: async function routeElement(match) {
          try {
            const mod = (await route()) as FileExports;
            const config: PathConfig = mod.config || defaultPageConfig;
            const useAuth = config.private || config.permission !== undefined;
            const AuthWrapper = useAuth ? Auth : NoopFragment;

            if (useAuth) {
              await auth.authStateReady();
            }

            return (
              <AuthWrapper params={match.params} config={config}>
                {mod.default ? <mod.default /> : "Nothing exported."}
              </AuthWrapper>
            );
          } catch (err) {
            console.error("element loading err", err);
            return null;
          }
        },
        loader: async function routeLoader(...args) {
          try {
            const mod = (await route()) as FileExports;
            const config: PathConfig = mod.config || defaultPageConfig;
            const useAuth = config.private || config.permission !== undefined;
            const id = semiRandomId();
            console.log(id, "loader init", mod.loader, Date.now() - start);
            if (mod.loader && useAuth) {
              console.log(id, "auth check", Date.now() - start);
              await auth.authStateReady();
              console.log(id, "auth ready", Date.now() - start);
            }
            console.log(id, "loader start", Date.now() - start, args);
            const l = await mod.loader?.(...args);
            console.log(id, "loader end", Date.now() - start);
            return l;
          } catch (err) {
            console.error("loading err", err);
            return null;
          }
        },
      };

      if (routeFragment) {
        // if fragment exits, add loaders to fragment so all children inherit them not just the "/" leaf
        routeFragment.loader = newRoute.loader;
        if (routeFragment.children) {
          routeFragment.children.push({ ...newRoute, loader: undefined });
        } else {
          routeFragment.children = [{ ...newRoute, loader: undefined }];
        }
      } else {
        acc.push({
          path: fragment,
          children: [newRoute],
        });
      }
      return acc;
    }

    if (routeFragment) {
      return routeFragment.children as Route[];
    } else {
      acc.push({
        path: fragment,
        children: [],
      });
      // oxlint-disable-next-line typescript/no-non-null-assertion
      return acc.find((rte) => rte.path == fragment)!.children as Route[];
    }
  }, routes);

  return routes;
}, []);

const location = new ReactLocation({
  history: createBrowserHistory({ window: globalThis.window }),
});

location.history.listen(() => {
  if (isAllowedCachedPath(location.current.pathname)) {
    setLocalStorageItem(LOCATION_RESTORE_KEY, location.current);
  }
});

const routes = [
  ...generatedRoutes,
  {
    path: "/",
    element: routeRedirectElement(),
  },
  {
    path: "*",
    element: routeRedirectElement(),
  },
];

/**
 * path validation and redirect up the tree (no extra bits)
 */
function isValidPath(path: string) {
  const fragments = path.replace(/^\//g, "").replace(/\/$/g, "").split("/");
  /**
   * - find all route paths that are valid partial matches (the starting is all the same but it ends in something maybe different)
   * - sort so the closest match is first then use that as the redirect target if its not an exact match
   *
   *           current : /o/TTT/t/TTT/
   *  unsorted matches : [o/:orgId, o/:orgId/t/:tenantId]
   *    sorted matches : [o/:orgId/t/:tenantId, o/:orgId]
   */
  const matchingPaths = regularRoutes
    .reduce<string[][]>((frags, x) => {
      const _pFrag = x.replace(/^\//g, "").replace(/\/$/g, "").split("/");
      if (
        _pFrag.every((_frag, idx) =>
          _frag.startsWith(":")
            ? Boolean(fragments[idx])
            : fragments[idx] === _frag,
        )
      ) {
        frags.push(_pFrag);
      }
      return frags;
    }, [])
    .sort((a, b) => b.length - a.length);

  const matchingPathFrags = matchingPaths[0];

  if (matchingPathFrags.length === fragments.length) {
    return true;
  }

  if (fragments.length > matchingPathFrags.length) {
    return `/${matchingPathFrags
      .map((_frag, idx) => (_frag.startsWith(":") ? fragments[idx] : _frag))
      .join("/")}`;
  }

  return "/";
}

const redirectPath = isValidPath(location.current.pathname);
if (typeof redirectPath === "string") {
  console.warn(
    `path invalid, redirecting: ${location.current.pathname} => ${redirectPath}`,
  );
  location.navigate({ ...location.current, pathname: redirectPath }, true);
}

export function isAllowedCachedPath(path?: string) {
  return (
    Boolean(path) &&
    path !== "/" &&
    !/^\/system-settings\/?$/.test(path || "") &&
    !path?.endsWith("/print")
  );
}

export const Routes = () => {
  /**
   * When app mounts we want to set the initial LOCATION
   * so we can restore to this location when app is closed and opened
   */
  useEffect(() => {
    if (isAllowedCachedPath(location.current.pathname)) {
      setLocalStorageItem(LOCATION_RESTORE_KEY, location.current);
    }
  }, []);

  return (
    <Router
      location={location}
      routes={routes}
      defaultPendingMs={200}
      defaultPendingMinMs={200}
      defaultPendingElement={<LoadingWrapper when={true} />}
    >
      <App>
        <OfflineSupportProvider>
          <Outlet />
        </OfflineSupportProvider>
      </App>
    </Router>
  );
};
